import { BaseFeature } from '../../core/base-feature.js';

/**
 * CorruptionFeature - Corruption node-clicking game (Frontend)
 * Handles: starting games, applying powerups/debuffs, monitoring progress
 */
export class CorruptionFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.currentGame = null;
        this.isLoading = false;

        // Spawn mode state
        this.spawnMode = false;
        this.selectedNodeType = 'normal';

        // Difficulty modifiers (real-time adjustable)
        this.difficultyModifiers = {
            spreadMultiplier: 1.0,
            clearMultiplier: 1.0,
            healthMultiplier: 1.0,
            clickDamageMultiplier: 1.0,
            autoSpawnInterval: 15000,
            maxNodes: 8,
            waveAcceleration: 0.1,
            nodeShieldChance: 0,
            nodeShieldDuration: 2000,
            corruptionRadius: 300
        };

        // Event listeners for React components
        this.stateCallbacks = new Set();
        this.updateCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'corruption',
            name: 'corruption',
            version: 'v1.1',
            capabilities: [
                'start_game',
                'end_game',
                'apply_powerup',
                'apply_debuff',
                'get_state',
                'spawn_mode',
                'spawn_node',
                'adjust_difficulty'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Corruption] Initializing...');
        console.log('[Corruption] Initialized');
    }

    async handleMessage(message) {
        const { action, payload, messageId, fromId } = message;

        switch (action) {
            case 'start_response':
                return this.handleStartResponse(payload);

            case 'end_response':
                return this.handleEndResponse(payload);

            case 'powerup_response':
                return this.handlePowerupResponse(payload);

            case 'debuff_response':
                return this.handleDebuffResponse(payload);

            case 'get_state_response':
                return this.handleGetStateResponse(payload);

            case 'state_update':
                return this.handleStateUpdate(payload);

            case 'game_complete':
                return this.handleGameComplete(payload);

            case 'initial_state':
                return this.handleInitialState(payload);

            default:
                console.warn(`[Corruption] Unknown action: ${action}`);
        }
    }

    // =====================================
    // Game Management
    // =====================================

    /**
     * Start a new game
     * @param {Object} config - Game configuration
     * @returns {Promise<Object>} Result
     */
    async startGame(config = {}) {
        console.log('[Corruption] Starting game', config);

        return new Promise((resolve) => {
            this.sendMessage('start', config);
            this._pendingRequests.set('start', resolve);
        });
    }

    handleStartResponse(payload) {
        const resolve = this._pendingRequests.get('start');
        if (resolve) {
            this._pendingRequests.delete('start');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[Corruption] Game started - waiting for state updates');
            // Don't set partial state here - wait for real state_update from desktop
            // The state_update handler will set currentGame with full data
        } else {
            console.error('[Corruption] Failed to start game:', payload.error);
        }
    }

    /**
     * End the current game
     * @returns {Promise<Object>} Result
     */
    async endGame() {
        console.log('[Corruption] Ending game');

        return new Promise((resolve) => {
            this.sendMessage('end', {});
            this._pendingRequests.set('end', resolve);
        });
    }

    handleEndResponse(payload) {
        const resolve = this._pendingRequests.get('end');
        if (resolve) {
            this._pendingRequests.delete('end');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[Corruption] Game ended');
            this.currentGame = null;
            this.notifyUpdateListeners();
        }
    }

    /**
     * Apply a powerup to help the desktop user
     * @param {string} powerupId - Powerup ID
     * @returns {Promise<Object>} Result
     */
    async applyPowerup(powerupId) {
        console.log(`[Corruption] Applying powerup: ${powerupId}`);

        if (!this.currentGame || this.currentGame.status !== 'active') {
            return { success: false, error: 'No active game' };
        }

        return new Promise((resolve) => {
            this.sendMessage('powerup', { type: powerupId });
            this._pendingRequests.set(`powerup_${powerupId}`, resolve);
        });
    }

    handlePowerupResponse(payload) {
        const resolve = this._pendingRequests.get(`powerup_${payload.effectId}`);
        if (resolve) {
            this._pendingRequests.delete(`powerup_${payload.effectId}`);
            resolve(payload);
        }

        if (payload.success) {
            console.log(`[Corruption] Powerup applied: ${payload.effectId}`);
        } else {
            console.warn('[Corruption] Powerup failed:', payload.error);
        }
    }

    /**
     * Apply a debuff to hinder the desktop user
     * @param {string} debuffId - Debuff ID
     * @param {Object} options - Additional options (e.g., position)
     * @returns {Promise<Object>} Result
     */
    async applyDebuff(debuffId, options = {}) {
        console.log(`[Corruption] Applying debuff: ${debuffId}`);

        if (!this.currentGame || this.currentGame.status !== 'active') {
            return { success: false, error: 'No active game' };
        }

        return new Promise((resolve) => {
            this.sendMessage('debuff', { type: debuffId, ...options });
            this._pendingRequests.set(`debuff_${debuffId}`, resolve);
        });
    }

    handleDebuffResponse(payload) {
        const resolve = this._pendingRequests.get(`debuff_${payload.effectId}`);
        if (resolve) {
            this._pendingRequests.delete(`debuff_${payload.effectId}`);
            resolve(payload);
        }

        if (payload.success) {
            console.log(`[Corruption] Debuff applied: ${payload.effectId}`);
        } else {
            console.warn('[Corruption] Debuff failed:', payload.error);
        }
    }

    /**
     * Get current game state
     * @returns {Promise<Object>} Current state
     */
    async getState() {
        console.log('[Corruption] Requesting current state');

        return new Promise((resolve) => {
            this.sendMessage('get_state', {});
            this._pendingRequests.set('get_state', resolve);
        });
    }

    handleGetStateResponse(payload) {
        const resolve = this._pendingRequests.get('get_state');
        if (resolve) {
            this._pendingRequests.delete('get_state');
            resolve(payload);
        }

        if (payload.success && payload.state) {
            this.currentGame = payload.state;
            this.notifyUpdateListeners();
        }
    }

    // =====================================
    // State Updates
    // =====================================

    handleStateUpdate(payload) {
        this.currentGame = payload;

        // Notify state listeners (real-time updates)
        this.notifyStateListeners(payload);

        // Check for completion
        if (payload.status !== 'active') {
            this.currentGame = null;
            this.notifyUpdateListeners();
        }
    }

    handleGameComplete(payload) {
        console.log('[Corruption] Game complete:', payload.result);

        this.currentGame = null;
        this.notifyUpdateListeners();

        // Emit completion event for React
        this.emit('gameComplete', payload);
    }

    handleInitialState(payload) {
        console.log('[Corruption] Received initial state from desktop');
        this.currentGame = payload.currentGame;
        this.isLoading = false;
        this.notifyUpdateListeners();
    }

    // =====================================
    // Spawn Mode
    // =====================================

    /**
     * Enable/disable spawn mode
     * When enabled, video clicks spawn nodes instead of selecting windows
     * @param {boolean} enabled - Whether spawn mode is enabled
     * @param {string} nodeType - Type of node to spawn (default: 'normal')
     */
    setSpawnMode(enabled, nodeType = 'normal') {
        console.log(`[Corruption] setSpawnMode called: enabled=${enabled}, nodeType=${nodeType}, wasSpawnMode=${this.spawnMode}`);
        this.spawnMode = enabled;
        this.selectedNodeType = nodeType;
        console.log(`[Corruption] Spawn mode now: ${this.spawnMode}, isSpawnMode getter: ${this.isSpawnMode}`);

        // Emit event for UI updates
        this.emit('spawnModeChange', { enabled, nodeType });
    }

    /**
     * Set the type of node to spawn
     * @param {string} nodeType - Node type (normal, fast, tank, splitter, phantom, boss)
     */
    setSelectedNodeType(nodeType) {
        this.selectedNodeType = nodeType;
        console.log(`[Corruption] Selected node type: ${nodeType}`);
        this.emit('spawnModeChange', { enabled: this.spawnMode, nodeType });
    }

    /**
     * Check if spawn mode is enabled
     * @returns {boolean}
     */
    get isSpawnMode() {
        return this.spawnMode;
    }

    /**
     * Get the selected node type for spawning
     * @returns {string}
     */
    getSelectedNodeType() {
        return this.selectedNodeType;
    }

    /**
     * Spawn a node at a specific position
     * Called from WindowControlFeature when spawn mode is active
     * @param {number} x - X coordinate (0-1 normalized)
     * @param {number} y - Y coordinate (0-1 normalized)
     * @param {string} nodeType - Type of node to spawn
     * @returns {Promise<Object>} Result
     */
    async spawnNodeAtPosition(x, y, nodeType = null) {
        const type = nodeType || this.selectedNodeType;
        console.log(`[Corruption] spawnNodeAtPosition called: x=${x.toFixed(3)}, y=${y.toFixed(3)}, type=${type}`);
        console.log(`[Corruption] currentGame:`, this.currentGame);

        if (!this.currentGame || this.currentGame.status !== 'active') {
            console.warn('[Corruption] Cannot spawn node - no active game. currentGame:', this.currentGame);
            return { success: false, error: 'No active game' };
        }

        console.log(`[Corruption] Sending spawn_node message to desktop...`);
        return new Promise((resolve) => {
            const messageId = `spawn_${Date.now()}`;
            this.sendMessage('spawn_node', { x, y, nodeType: type });
            this._pendingRequests.set(messageId, resolve);

            // Auto-resolve after timeout (in case no response)
            setTimeout(() => {
                if (this._pendingRequests.has(messageId)) {
                    this._pendingRequests.delete(messageId);
                    console.log('[Corruption] spawn_node timed out, assuming success');
                    resolve({ success: true }); // Assume success if no error
                }
            }, 1000);
        });
    }

    // =====================================
    // Difficulty Adjustment
    // =====================================

    /**
     * Adjust difficulty parameters in real-time
     * @param {string} parameter - Parameter name
     * @param {number} value - New value
     */
    adjustDifficulty(parameter, value) {
        if (this.difficultyModifiers.hasOwnProperty(parameter)) {
            this.difficultyModifiers[parameter] = value;
            console.log(`[Corruption] Adjusted ${parameter} to ${value}`);

            // Send to desktop
            this.sendMessage('adjust_difficulty', {
                ...this.difficultyModifiers
            });

            // Emit event for UI updates
            this.emit('difficultyChange', this.difficultyModifiers);
        } else {
            console.warn(`[Corruption] Unknown difficulty parameter: ${parameter}`);
        }
    }

    /**
     * Set all difficulty modifiers at once
     * @param {Object} modifiers - Difficulty modifier object
     */
    setDifficultyModifiers(modifiers) {
        this.difficultyModifiers = {
            ...this.difficultyModifiers,
            ...modifiers
        };
        console.log('[Corruption] Updated difficulty modifiers:', this.difficultyModifiers);

        // Send to desktop
        this.sendMessage('adjust_difficulty', this.difficultyModifiers);

        // Emit event for UI updates
        this.emit('difficultyChange', this.difficultyModifiers);
    }

    /**
     * Get current difficulty modifiers
     * @returns {Object}
     */
    getDifficultyModifiers() {
        return { ...this.difficultyModifiers };
    }

    // =====================================
    // Event Listeners
    // =====================================

    /**
     * Subscribe to real-time state updates (30fps)
     * @param {Function} callback - Called with state data
     * @returns {Function} Unsubscribe function
     */
    onStateUpdate(callback) {
        this.stateCallbacks.add(callback);
        return () => this.stateCallbacks.delete(callback);
    }

    /**
     * Subscribe to update events (game start/end)
     * @param {Function} callback - Called when state changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyStateListeners(state) {
        for (const callback of this.stateCallbacks) {
            try {
                callback(state);
            } catch (error) {
                console.error('[Corruption] Error in state callback:', error);
            }
        }
    }

    notifyUpdateListeners() {
        const data = {
            currentGame: this.currentGame,
            isLoading: this.isLoading
        };

        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Corruption] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getCurrentGame() {
        return this.currentGame;
    }

    getIsLoading() {
        return this.isLoading;
    }

    isGameActive() {
        return this.currentGame && this.currentGame.status === 'active';
    }

    // =====================================
    // Utility Functions
    // =====================================

    /**
     * Format time as M:SS
     * @param {number} ms - Milliseconds
     * @returns {string} Formatted time
     */
    static formatTime(ms) {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }

    /**
     * Get difficulty preset
     * @param {string} difficulty - Difficulty level
     * @returns {Object} Configuration preset
     */
    static getDifficultyPreset(difficulty) {
        const presets = {
            easy: { lockoutMinutes: 2 },
            medium: { lockoutMinutes: 5 },
            hard: { lockoutMinutes: 10 },
            nightmare: { lockoutMinutes: 15 }
        };
        return presets[difficulty] || presets.medium;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[Corruption] Cleaning up...');
        this.currentGame = null;
        this.stateCallbacks.clear();
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
